/**
 * Analyzer: Confluence Consensus
 *
 * Finds confluences that were present on EVERY trade in the session.
 * Two actionable variants:
 *   - Universal confluence + high win rate → A-list combo, keep it
 *   - Universal confluence + low win rate → that combo failed today,
 *     don't assume it's automatic validation next time
 *
 * Bonus: also flags cases where winners share a confluence that losers
 * lack — "winning combo" signal for fine-grained play-style learning.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../types.js';
import { countAcrossTrades } from '../utils.js';

export const key = 'confluenceConsensus';

const resolveName = (id, lib) => lib.find(c => c.id === id || c.name === id)?.name || id;

// Map a confluence label back to its canonical id (or fall back to label
// when no library entry exists — keeps custom confluences working).
const resolveId = (label, lib) => {
  const hit = lib.find(c => c.id === label || c.name === label);
  return hit?.id || label;
};

/**
 * Build the structured "Pin to setup" actions for a confluence-consensus
 * observation. One action per setup present in the group's trades — clicking
 * a button pins all of `confluenceLabels` as required confluences for that
 * setup. The handler in TradingJournal calls `toggleSetupConfluence` per
 * (setup, confluence) pair to apply.
 *
 * Filters out setups where every requested confluence is ALREADY pinned
 * (reads `setupDetails[setupName].recommendedConfluences`). Without that
 * filter, the action would keep showing after the pin succeeded — the
 * ObservationActions component briefly flashes "Pinned ✓" then reverts to
 * the original "Pin to..." label because the analyzer keeps emitting it.
 * Hiding the button when the work is done is the cleaner UX than trying to
 * persist a one-shot "Pinned" state inside the button.
 */
const buildPinActions = (trades, allConfluences, confluenceLabels, setupDetails = {}) => {
  if (!Array.isArray(trades) || trades.length === 0) return null;
  if (!Array.isArray(confluenceLabels) || confluenceLabels.length === 0) return null;

  // Distinct setups present in this group, preserving first-seen order.
  const setups = [];
  const seen = new Set();
  for (const t of trades) {
    const s = (t?.setup || '').trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    setups.push(s);
  }
  if (setups.length === 0) return null;

  const confluenceIds = confluenceLabels.map(label => resolveId(label, allConfluences));
  const confluenceTextList = confluenceLabels.length === 1
    ? `"${confluenceLabels[0]}"`
    : `"${confluenceLabels.slice(0, 2).join('" + "')}"`;

  // Drop setups where every requested confluence is already pinned —
  // ID match against the stored recommendedConfluences. Also matches by
  // label as a fallback because custom confluences without a library
  // entry resolve to their label string (see resolveId above).
  const stillNeeded = setups.filter(setupName => {
    const pinned = new Set(setupDetails?.[setupName]?.recommendedConfluences || []);
    return !confluenceIds.every(cid => pinned.has(cid));
  });
  if (stillNeeded.length === 0) return null;

  return stillNeeded.map(setupName => makeAction({
    type: ACTION_TYPE.PIN_CONFLUENCE_TO_SETUP,
    label: stillNeeded.length === 1 ? `Pin to "${setupName}"` : `Pin to "${setupName}"`,
    payload: { setupName, confluenceIds, confluenceLabels },
    confirmText: `Pin ${confluenceTextList} as required confluence${confluenceLabels.length > 1 ? 's' : ''} on "${setupName}"?`,
    doneLabel: 'Pinned ✓',
  }));
};

export const analyze = (ctx) => {
  const { session, trades, enriched, summary, allConfluences = [], setupDetails = {} } = ctx;
  if (!session || !Array.isArray(trades) || trades.length === 0) return null;

  // Live-trade gate: this analyzer makes outcome claims ("0% won",
  // "winning combo on every winner"). Firing those while any trade in the
  // cohort is still live produces premature claims based on mark-to-market
  // unrealized P&L — actively panic-inducing. Defer until all trades close.
  if (summary?.pending > 0) return null;

  const { winRate, total } = summary;
  // Confluence universality + winner/loser classification operate on
  // CLOSED trades only — consistent population with the WR + P&L stats
  // pulled from summary above. (`pending` was already 0 per the gate.)
  const closedTrades = trades.filter(t => t?.isOpen !== true);
  const closedEnriched = Array.isArray(enriched)
    ? enriched.filter(x => x?.trade?.isOpen !== true)
    : [];
  const confCounts = countAcrossTrades(closedTrades, 'confluences');
  if (confCounts.size === 0) return null;

  // Implied-confluence filter: a confluence ID that's implied by every
  // setup in the group is tautologically universal — the chip was auto-
  // applied by the setup definition, not picked by the trader. Surfacing
  // "X was on every trade" or "X was on every winner" in that case is
  // pure noise (and confusing — the setup-level analyzer already carries
  // the relevant signal). Build the filter once per call.
  //
  // The check considers ALL trades' setups (not just trades carrying the
  // confluence): if any trade's setup does NOT imply the confluence yet
  // the confluence shows up on every trade, then the trader explicitly
  // tagged it on that trade — keep it as a real signal.
  const isImpliedAcrossAll = (confId, group) => {
    if (!Array.isArray(group) || group.length === 0) return false;
    return group.every(t => {
      const implied = setupDetails?.[t?.setup]?.impliedConfluences;
      return Array.isArray(implied) && implied.includes(confId);
    });
  };

  // Universal = present on every (closed) trade in the session, excluding
  // any confluence that's implied by every setup in the closed group.
  const universal = Array.from(confCounts.entries())
    .filter(([, cnt]) => cnt === closedTrades.length)
    .filter(([id]) => !isImpliedAcrossAll(id, closedTrades))
    .map(([id]) => resolveName(id, allConfluences));

  // ─── Winning-combo signal (new) ───
  // Find confluences that appear on winners but are absent from losers.
  // Requires at least 2 winners and 2 losers for meaningful contrast.
  // Filter `isOpen` defensively here too — even though the early gate
  // catches sessions with pending trades, the per-bucket filter makes the
  // intent explicit and protects against future caller-side regressions.
  const winners = closedEnriched.filter(x => x.netPnl > 0).map(x => x.trade);
  const losers = closedEnriched.filter(x => x.netPnl < 0).map(x => x.trade);
  if (winners.length >= 2 && losers.length >= 2) {
    const winCounts = countAcrossTrades(winners, 'confluences');
    const loseCounts = countAcrossTrades(losers, 'confluences');
    const winOnlyKeys = Array.from(winCounts.entries())
      .filter(([id, cnt]) => cnt === winners.length && (loseCounts.get(id) || 0) === 0)
      // If every winner's setup implies this confluence, the "winners-only"
      // pattern just reflects the setup difference (winners took setup A
      // which implies X; losers took setup B which doesn't). Setup analyzer
      // already explains that — confluence-level claim would mislead.
      .filter(([id]) => !isImpliedAcrossAll(id, winners))
      .map(([id]) => resolveName(id, allConfluences));

    if (winOnlyKeys.length > 0) {
      const comboLabel = winOnlyKeys.slice(0, 2).join(' + ');
      const pinActions = buildPinActions(trades, allConfluences, winOnlyKeys.slice(0, 2), setupDetails) || [];
      return makeObservation({
        id: `${key}:${session.id}:win-only`,
        analyzer: key,
        scope: 'session',
        scopeId: session.id,
        severity: SEVERITY.SUCCESS,
        category: CATEGORY.CONFLUENCE,
        title: `"${comboLabel}" was on every winner, none of the losers`,
        detail: `${winners.length} winners vs ${losers.length} losers — that combo is doing real work, not riding along.`,
        action: 'Tag this combo on your A-list. Require it as a setup filter before pulling trigger next time.',
        actions: [
          ...pinActions,
          makeAction({
            type: ACTION_TYPE.ADD_LESSON_TO_SESSION,
            label: 'Save to Lessons',
            payload: {
              sessionId: session.id,
              lesson: `"${comboLabel}" was on every winner today, absent from losers — treat it as a hard filter for this playbook`,
            },
            doneLabel: 'Saved ✓',
          }),
        ],
        evidence: { winOnlyKeys, winners: winners.length, losers: losers.length },
        confidence: 0.88,
        priority: 82
      });
    }
  }

  // ─── Universal + strong outcome (existing behavior) ───
  if (universal.length > 0 && winRate >= 0.6 && total > 0) {
    const comboLabel = universal.slice(0, 2).join(' + ');
    const pinActions = buildPinActions(trades, allConfluences, universal.slice(0, 2), setupDetails) || [];
    return makeObservation({
      id: `${key}:${session.id}:universal-good`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.CONFLUENCE,
      title: `"${comboLabel}" on every trade, ${(winRate * 100).toFixed(0)}% wins`,
      detail: `Consistent presence with strong outcome — this combo earned its place on your A-list.`,
      action: 'Pin this as a required confluence in your highest-conviction setup.',
      actions: [
        ...pinActions,
        makeAction({
          type: ACTION_TYPE.ADD_LESSON_TO_SESSION,
          label: 'Save to Lessons',
          payload: {
            sessionId: session.id,
            lesson: `"${comboLabel}" on every trade with ${(winRate * 100).toFixed(0)}% WR — this combo is the edge, not just along for the ride`,
          },
          doneLabel: 'Saved ✓',
        }),
      ],
      evidence: { universal, winRate, total },
      confidence: 0.78,
      priority: 65
    });
  }

  // ─── Universal + weak outcome ───
  if (universal.length > 0 && winRate <= 0.3 && total < 0) {
    return makeObservation({
      id: `${key}:${session.id}:universal-fail`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.INFO,
      category: CATEGORY.CONFLUENCE,
      title: `"${universal.slice(0, 2).join(' + ')}" was everywhere but only ${(winRate * 100).toFixed(0)}% won`,
      detail: `That confluence failed today — it's not an automatic green light next time this tape shows up.`,
      action: 'Review what context broke this signal; add a gating filter before it counts as valid.',
      evidence: { universal, winRate, total },
      confidence: 0.78,
      priority: 68
    });
  }

  return null;
};
